import { Media } from "../types/media";
import { MediaSortBy } from "../types/mediaSort";
import { cameraLocalDayKey } from "./cameraLocalDateTime";

export const UNKNOWN_DAY_KEY = "unknown";

export interface MediaDaySection<T> {
  key: string;
  dayKey: string;
  label: string;
  items: T[];
}

export function shouldGroupMediaByDay(sortBy: MediaSortBy): boolean {
  return sortBy === "takenAt" || sortBy === "uploadedAt";
}

function localDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function parseFlexibleDate(raw: string | undefined): Date | null {
  if (!raw) {
    return null;
  }
  const value = raw.trim();
  const exifMatch = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(value);
  if (exifMatch) {
    const [, year, month, day, hour, minute, second] = exifMatch;
    const date = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute), Number(second));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function takenAtDayKey(raw: string | undefined): string | null {
  return cameraLocalDayKey(raw);
}

export function mediaDayKey(media: Media, sortBy: MediaSortBy): string {
  if (sortBy === "uploadedAt") {
    const uploaded = parseFlexibleDate(media.createdAt);
    return uploaded ? localDayKey(uploaded) : UNKNOWN_DAY_KEY;
  }

  return takenAtDayKey(media.metadata?.takenAt) ?? UNKNOWN_DAY_KEY;
}

export function formatMediaDayLabel(dayKey: string): string {
  if (dayKey === UNKNOWN_DAY_KEY) {
    return "Unknown date";
  }

  const [year, month, day] = dayKey.split("-").map(Number);
  if (!year || !month || !day) {
    return "Unknown date";
  }

  const date = new Date(year, month - 1, day);
  if (Number.isNaN(date.getTime())) {
    return "Unknown date";
  }

  const weekday = date.toLocaleDateString(undefined, { weekday: "short" });
  const paddedMonth = String(month).padStart(2, "0");
  const paddedDay = String(day).padStart(2, "0");
  return `${year}-${paddedMonth}-${paddedDay} ${weekday}`;
}

export function groupMediaByDay<T>(items: T[], getDayKey: (item: T) => string): MediaDaySection<T>[] {
  const sections: MediaDaySection<T>[] = [];
  const indexByDay = new Map<string, number>();

  for (const item of items) {
    const dayKey = getDayKey(item);
    const existingIndex = indexByDay.get(dayKey);
    if (existingIndex !== undefined) {
      sections[existingIndex].items.push(item);
      continue;
    }

    indexByDay.set(dayKey, sections.length);
    sections.push({
      key: dayKey,
      dayKey,
      label: formatMediaDayLabel(dayKey),
      items: [item],
    });
  }

  return sections;
}
